import React from 'react';
import {
  FormProvider,
  UseFormReturn,
  FieldValues,
  Controller,
  ControllerProps,
  FieldPath,
} from 'react-hook-form';
import { cn } from '@/app/lib/utils';

export interface FormProps<
  TFieldValues extends FieldValues,
  TContext = unknown,
> extends React.FormHTMLAttributes<HTMLFormElement> {
  form: UseFormReturn<TFieldValues, TContext>;
}

const Form = <TFieldValues extends FieldValues, TContext = unknown>({
  form,
  children,
  ...otherProps
}: FormProps<TFieldValues, TContext>) => {
  return (
    <FormProvider {...form}>
      <form {...otherProps}>{children}</form>
    </FormProvider>
  );
};

export const FormItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  showErrorMessage = true,
  className,
  render,
  label,
  labelClassName,
  ...props
}: {
  showErrorMessage?: boolean;
  className?: string;
  label?: React.ReactNode | string;
  labelClassName?: string;
} & ControllerProps<TFieldValues, TName>) => {
  return (
    <Controller
      {...props}
      render={args => {
        return (
          <div className={className} data-testid={`form-item-${props.name}`}>
            {label && (
              <div className={cn('text-sm text-muted-foreground mb-1', labelClassName)}>
                {label}
              </div>
            )}
            {render(args)}
            {args.fieldState.error && showErrorMessage && (
              <span className="text-xs text-red-500">{args.fieldState.error.message}</span>
            )}
          </div>
        );
      }}
    />
  );
};

export default Form;
